import { PanelConfiguration, PanelMetaBase } from ".";
import { assertNever } from "../helper/assertNever";
import { addCh9TVCollConfig } from "../projects/C9AU-Channel9";
import { addFinecast1121CollConfig } from "../projects/FINE-1121-Finecast";
import { addCh7TVCollConfig } from "../projects/C7AU-Channel7"
import { RespondentConfigBuilder } from "../respondentConfigBuilder";
import { addFoxtelCollConfig } from "../projects/FXTL-0722-Foxtel";
import { addViab0822CollBvodMobileConfig, addViab0822LinearTVConfig } from "../projects/VIAB-0822";
import { addOmnz1022TVConfig } from "../projects/OMNZ-1022";
import { addSKYN0823CollConfig } from "../projects/SKYN-0823";
import { addRCTV0523CollConfig } from "../projects/RCTV-0523";
import { addCART1025CollConfig } from "../projects/CART-1025";
import { addCINE0226CollConfig, CINE0226AmplifiedCollGroupIds } from "../projects/CINE-0226";

const amplifiedTVGroupIds = [
    'C9AU-25-1021-LINEARTV',
    'C9AU-26-1021-BVODTV',
    'FINE-28-1121-LINEARTV',
    'FINE-29-1121-BVODTV',
    'FINE-1121-UPLOAD',
    'C7AU-62-0122-LINEARTV',
    'C7AU-63-0122-CTV',
    "FXTL-140-FOXTEL-LIVE",
    "FXTL-141-KAYO-LIVE",
    "FXTL-142-FTA-LIVE",
    "FXTL-143-BVOD-TV",
    "FXTL-145-BVOD-M",
    "VIAB-206-0822-BE-NORTH-BVOD-M",
    "VIAB-207-0822-BE-SOUTH-BVOD-M",
    "OMNZ-215-TVNZ-LIVE",
    "OMNZ-216-BVOD-TV",
    "VIAB-245-0822-BE-NORTH-LINEARTV",
    "VIAB-246-0822-BE-SOUTH-LINEARTV",
    "SKYN-296-0723-BVOD-MOBILE",
    "RCTV-297-0523-BVODMOBILE"
] as const;

export type AmplifiedTVGroupId = typeof amplifiedTVGroupIds[number];

export function isAmplifiedTVGroupId(obj: unknown): obj is AmplifiedTVGroupId {
    return (
        typeof obj === 'string' &&
        (amplifiedTVGroupIds as ReadonlyArray<string>).includes(obj)
    )
}

export function isAmplifiedGroupId(obj: unknown): obj is AmplifiedTVGroupId | AmplifiedMobileGroupId {
    return isAmplifiedTVGroupId(obj) || isAmplifiedMobileGroupId(obj);
}

const amplifiedTVExtraMetaKeys = [
    'panelRespondentID',
    'panelIdentifier',
    'viewingNumber'
] as const;

export type AmplifiedTVExtraMetaKey = typeof amplifiedTVExtraMetaKeys[number];

export type PanelMetaAmplifiedTV = PanelMetaBase<AmplifiedTVGroupId> & {
    panelRespondentId: string
    panelIdentifier: string
    viewingNumber: number
    user?: string
    email?: string
};

export class PanelConfigurationAmplifiedTV implements PanelConfiguration {
    constructor(
        private meta: PanelMetaAmplifiedTV
    ) {}

    addExtraMetaAndFlags(configBuilder: RespondentConfigBuilder<AmplifiedTVExtraMetaKey>) {
        configBuilder
            .addExtraMeta('panelRespondentID', this.meta.panelRespondentId)
            .addExtraMeta('panelIdentifier', this.meta.panelIdentifier)
            .addExtraMeta('viewingNumber', `${this.meta.viewingNumber}`)
    }

    addCompletionTasks(configBuilder: RespondentConfigBuilder<AmplifiedTVExtraMetaKey>) {
        // Amplified TV Panel has no tasks to add
    }

    addGroupConfig(configBuilder: RespondentConfigBuilder<AmplifiedTVExtraMetaKey>): void {
        switch (this.meta.groupId) {
            case 'C9AU-25-1021-LINEARTV':
            case 'C9AU-26-1021-BVODTV': {
                addCh9TVCollConfig(
                    this.meta.groupId,
                    this.meta.viewingNumber,
                    amplifiedTVExtraMetaKeys,
                    configBuilder
                )

                break;
            };

            case 'FINE-28-1121-LINEARTV':
            case 'FINE-29-1121-BVODTV':
            case 'FINE-1121-UPLOAD': {
                addFinecast1121CollConfig(
                    this.meta.groupId,
                    this.meta.viewingNumber,
                    amplifiedTVExtraMetaKeys,
                    configBuilder
                )
                break;
            }

            case 'C7AU-62-0122-LINEARTV':
            case 'C7AU-63-0122-CTV': {
                if (this.meta.user == undefined || this.meta.email == undefined) {
                    throw new Error('C7AU name and email should have values')
                }

                addCh7TVCollConfig(
                    this.meta.groupId,
                    this.meta.viewingNumber,
                    amplifiedTVExtraMetaKeys,
                    configBuilder,
                    this.meta.user,
                    this.meta.email
                )

                break;
            };

            case 'FXTL-140-FOXTEL-LIVE':
            case 'FXTL-141-KAYO-LIVE':
            case 'FXTL-142-FTA-LIVE':
            case "FXTL-143-BVOD-TV":
            case "FXTL-145-BVOD-M": {
                if (this.meta.user == undefined || this.meta.email == undefined) {
                    throw new Error('FXTL name and email should have values')
                }

                addFoxtelCollConfig({
                    groupId: this.meta.groupId,
                    viewingCount: this.meta.viewingNumber,
                    metaFieldNames: amplifiedTVExtraMetaKeys,
                    user: this.meta.user,
                    email: this.meta.email
                }, configBuilder)

                break;
            }

            case "VIAB-206-0822-BE-NORTH-BVOD-M":
            case "VIAB-207-0822-BE-SOUTH-BVOD-M":{
                if (this.meta.user == undefined || this.meta.email == undefined) {
                    throw new Error('VIAB BVOD name and email should have values')
                }

                addViab0822CollBvodMobileConfig({
                    groupId: this.meta.groupId,
                    viewingCount: this.meta.viewingNumber,
                    metaFieldNames: amplifiedTVExtraMetaKeys,
                    user: this.meta.user,
                    email: this.meta.email
                }, configBuilder)

                break;
            }

            case "OMNZ-215-TVNZ-LIVE":
            case "OMNZ-216-BVOD-TV": {
                if (this.meta.user == undefined || this.meta.email == undefined) {
                    throw new Error('OMNZ name and email should have values');
                }

                addOmnz1022TVConfig({
                    groupId: this.meta.groupId,
                    viewingCount: this.meta.viewingNumber,
                    metaFieldNames: amplifiedTVExtraMetaKeys,
                    user: this.meta.user,
                    email: this.meta.email
                }, configBuilder)

                break;
            }

            case "VIAB-245-0822-BE-NORTH-LINEARTV":
            case "VIAB-246-0822-BE-SOUTH-LINEARTV": {
                if (this.meta.user == undefined || this.meta.email == undefined) {
                    throw new Error('VIAB LINEARTV name and email should have values');
                }

                addViab0822LinearTVConfig({
                    groupId: this.meta.groupId,
                    viewingCount: this.meta.viewingNumber,
                    metaFieldNames: amplifiedTVExtraMetaKeys,
                    user: this.meta.user,
                    email: this.meta.email
                }, configBuilder)

                break;
            };

            case "SKYN-296-0723-BVOD-MOBILE": {
                addSKYN0823CollConfig({
                    groupId: this.meta.groupId,
                    metaFieldNames: ['panelRespondentID', 'panelIdentifier']
                }, configBuilder)
                break;
            }

            case "RCTV-297-0523-BVODMOBILE": {
                addRCTV0523CollConfig({
                    groupId: this.meta.groupId,
                    metaFieldNames: ['panelRespondentID', 'panelIdentifier']
                }, configBuilder)
                break;
            }

            default: assertNever(this.meta.groupId);
        }
    }
}

const amplifiedMobileGroupIds = [
	"CART-461-1025-AU-WOOLWORTHS-ALL",
	...CINE0226AmplifiedCollGroupIds,
] as const;

export type AmplifiedMobileGroupId = typeof amplifiedMobileGroupIds[number];

export function isAmplifiedMobileGroupId(obj: unknown): obj is AmplifiedMobileGroupId {
    return (
        typeof obj === 'string' &&
        (amplifiedMobileGroupIds as ReadonlyArray<string>).includes(obj)
    )
}

const amplifiedMobileExtraMetaKeys = [
    'panelRespondentID',
    'panelIdentifier',
	'loginId',
	'groupID'
] as const;

export type AmplifiedMobileExtraMetaKey = typeof amplifiedMobileExtraMetaKeys[number];

export type PanelMetaAmplifiedMobile = PanelMetaBase<AmplifiedMobileGroupId> & {
    panelRespondentId: string
    panelIdentifier: string
};

export class PanelConfigurationAmplifiedMobile implements PanelConfiguration {
    constructor(
        private meta: PanelMetaAmplifiedMobile
    ) {}

	addExtraMetaAndFlags(configBuilder: RespondentConfigBuilder<AmplifiedMobileExtraMetaKey>) {
        configBuilder
            .addExtraMeta('panelRespondentID', this.meta.panelRespondentId)
            .addExtraMeta('panelIdentifier', this.meta.panelIdentifier)
			.addExtraMeta('groupID', this.meta.groupId)
			// loginId will be filled in later by the LoginID API Toolkit, so we can leave it as an empty string for now
    }

    addCompletionTasks(configBuilder: RespondentConfigBuilder<AmplifiedTVExtraMetaKey>) {
        // Amplified Mobile Panel has no complete tasks to add
    }

    addGroupConfig(configBuilder: RespondentConfigBuilder<AmplifiedMobileExtraMetaKey>): void {
        switch (this.meta.groupId) {
            case 'CART-461-1025-AU-WOOLWORTHS-ALL':
				addCART1025CollConfig({
					groupId: this.meta.groupId,
					metaFieldNames: amplifiedMobileExtraMetaKeys,
				}, configBuilder)
				break;

			case 'CINE-462-0226-CA-YAHOO-RETARGETING':
				addCINE0226CollConfig({
					groupId: this.meta.groupId,
					metaFieldNames: amplifiedMobileExtraMetaKeys,
				}, configBuilder);
				break;
				
			default: assertNever(this.meta.groupId);
		}
	}
}